import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInvoiceStore } from '../stores/invoiceStore';
import { useAuthStore } from '../stores/authStore';
import { useTranslation } from '../hooks/useTranslation';
import { Check, ExternalLink, Loader2 } from 'lucide-react';
import { FloatingLabelInput } from './shared/FloatingLabelInput';
import InlineEdit from './InlineEdit';
import { mollieApi } from '../lib/api';
import { LoginRequiredDialog } from './LoginRequiredDialog';
import { 
  PAYMENT_METHOD_TYPES,
  initializePaymentMethods,
  getEnabledPaymentMethods,
  cleanPaymentMethods,
  updateSettingsWithPaymentMethods
} from '../utils/paymentMethodUtils';
import type { PaymentMethodType, PaymentMethods } from '@dutchinvoice/types';

// Temporarily hide these payment methods
const TEMP_HIDE_PAYMENTS: PaymentMethodType[] = ['stripe', 'paypal'];

interface PaymentMethodEditProps {
  isEditing: boolean;
  onEditClick: () => void;
  onSave: () => void;
}

const PaymentMethodEdit: React.FC<PaymentMethodEditProps> = ({
  isEditing,
  onEditClick,
  onSave,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const currentInvoice = useInvoiceStore((state) => state.currentInvoice);
  const settings = useInvoiceStore((state) => state.settings);
  const updateInvoice = useInvoiceStore((state) => state.updateInvoice);
  const updateSettings = useInvoiceStore((state) => state.updateSettings);
  const getNextInvoiceNumber = useInvoiceStore((state) => state.getNextInvoiceNumber);
  
  const isPlaceholder = currentInvoice.isPlaceholder?.paymentMethods;
  const hasPaymentMethods = currentInvoice.paymentMethods && 
                           getEnabledPaymentMethods(currentInvoice.paymentMethods).length > 0 && 
                           !isPlaceholder;
  
  // Use current invoice number if it exists, otherwise show next number
  const displayNumber = currentInvoice.invoiceNumber || getNextInvoiceNumber();
  
  // Get payment terms days
  const paymentTermsDays = currentInvoice.paymentTerms ? parseInt(currentInvoice.paymentTerms, 10) : 14;

  // State for payment methods
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethods>(() => 
    initializePaymentMethods(currentInvoice.paymentMethods, settings)
  );
  
  // State for "Add as default for future invoices" checkboxes
  const [defaultMethods, setDefaultMethods] = React.useState<PaymentMethodType[]>(
    settings?.defaultPaymentMethods || []
  );

  // Add a state for loading the Mollie connect button
  const [isMollieConnecting, setIsMollieConnecting] = React.useState(false);
  const [showMollieLoginDialog, setShowMollieLoginDialog] = React.useState(false);
  
  const handleFieldChange = (type: PaymentMethodType, field: string, value: string | boolean) => {
    setPaymentMethods((prev: PaymentMethods) => {
      const result = { ...prev };
      
      // Use type assertions more specifically
      if (!result[type as keyof PaymentMethods]) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result[type as keyof PaymentMethods] = {} as any;
      }
      
      const methodData = result[type as keyof PaymentMethods];
      if (methodData) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (methodData as any)[field] = value;
      }
      
      return result;
    });
  };
  
  // Toggle a payment method as default
  const toggleDefaultMethod = (type: PaymentMethodType, isDefault: boolean) => {
    if (isDefault) {
      setDefaultMethods(prev => [...prev, type]);
    } else {
      setDefaultMethods(prev => prev.filter(method => method !== type));
    }
  };
  
  // Add a function to handle Mollie connection
  const handleMollieConnect = async () => {
    if (!isAuthenticated) {
      setShowMollieLoginDialog(true);
      return;
    }
    
    try {
      setIsMollieConnecting(true);
      const { url } = await mollieApi.getAuthUrl();
      // Don't set loading to false here anymore, keep it loading
      window.location.href = url;
    } catch (error) {
      console.error('Failed to connect to Mollie:', error);
      setIsMollieConnecting(false); // Only set to false on error
    }
  };
  
  const handleSave = () => {
    // Clean payment methods (remove disabled methods)
    const cleanedPaymentMethods = cleanPaymentMethods(paymentMethods);
    
    // Update the invoice
    updateInvoice({
      ...currentInvoice,
      paymentMethods: cleanedPaymentMethods,
      isPlaceholder: {
        ...currentInvoice.isPlaceholder,
        paymentMethods: false
      }
    });
    
    // Update settings with payment methods and default payment methods
    if (settings) {
      updateSettings(updateSettingsWithPaymentMethods(
        settings,
        paymentMethods,
        defaultMethods
      ));
    }
    
    onSave();
  };

  const renderPaymentMethodDisplay = () => {
    if (!hasPaymentMethods) {
      return null;
    }

    // Get all enabled payment methods
    const enabledMethods = getEnabledPaymentMethods(currentInvoice.paymentMethods);
    
    return (
      <div className="space-y-6">
        {renderPaymentMethodCards(enabledMethods)}
      </div>
    );
  };
  
  const renderPaymentMethodCards = (enabledMethods: PaymentMethodType[]) => {
    // Determine if we have multiple payment methods
    const hasMultipleMethods = enabledMethods.length > 1;
    // Check if we only have online payment methods (no bank transfer)
    const hasOnlyOnlinePayments = enabledMethods.length > 0 && !enabledMethods.includes('bank');
    // Check if we need to show payment method in button title
    const showMethodInButton = (enabledMethods.length > 1 && !enabledMethods.includes('bank')) || 
                              (enabledMethods.length > 2 && enabledMethods.includes('bank'));
    
    return enabledMethods.map((methodType, i) => {
      // Determine if this is the first payment method
      const isFirstMethod = i === 0;
      
      switch (methodType) {
        case 'bank':
          if (!currentInvoice.paymentMethods?.bank) return null;
          return (
            <div key="bank" className="invoice-payment-details text-gray-600">
              <p className="text-sm mb-3">
                {t('invoice.paymentMethods.bank.paymentInstructionWithDays', { days: paymentTermsDays })}
              </p>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                <div className="min-w-[160px]">
                  <span className="text-xs uppercase tracking-wide text-gray-400">{t('invoice.paymentMethods.bank.accountName')}</span>
                  <p className="text-sm font-medium mt-0.5">{currentInvoice.paymentMethods.bank.accountName}</p>
                </div>
                <div className="min-w-[200px]">
                  <span className="text-xs uppercase tracking-wide text-gray-400">{t('invoice.paymentMethods.bank.iban')}</span>
                  <p className="text-sm font-mono tracking-wide mt-0.5 whitespace-nowrap">{currentInvoice.paymentMethods.bank.iban}</p>
                </div>
                {currentInvoice.paymentMethods.bank.bic && currentInvoice.paymentMethods.bank.bic.trim() !== '' && (
                  <div className="min-w-[110px]">
                    <span className="text-xs uppercase tracking-wide text-gray-400">BIC</span>
                    <p className="text-sm font-mono tracking-wide mt-0.5">{currentInvoice.paymentMethods.bank.bic}</p>
                  </div>
                )}
                <div className="min-w-[140px]">
                  <span className="text-xs uppercase tracking-wide text-gray-400">{t('invoice.paymentMethods.bank.reference')}</span>
                  <p className="text-sm font-medium mt-0.5">{displayNumber}</p>
                </div>
              </div>
            </div>
          );
        case 'paypal':
          if (!currentInvoice.paymentMethods?.paypal) return null;
          return (
            <div key="paypal" className="invoice-payment-details text-gray-600">
              <p className="text-sm mb-3">
                {hasOnlyOnlinePayments && isFirstMethod
                  ? t('invoice.paymentMethods.onlineOnly', { days: paymentTermsDays })
                  : hasMultipleMethods && !isFirstMethod 
                    ? t('invoice.paymentMethods.alternative') 
                    : t('invoice.paymentMethods.online')}
              </p>
              <button className="btn-primary min-w-[200px] justify-center">
                <span>
                  {showMethodInButton ? `${t('invoice.paymentMethods.payNow')} - PayPal` : t('invoice.paymentMethods.payNow')}
                </span>
              </button>
            </div>
          );
        case 'mollie':
          if (!currentInvoice.paymentMethods?.mollie) return null;
          return (
            <div key="mollie" className="invoice-payment-details text-gray-600">
              <p className="text-sm mb-3">
                {hasOnlyOnlinePayments && isFirstMethod
                  ? t('invoice.paymentMethods.onlineOnly', { days: paymentTermsDays })
                  : hasMultipleMethods && !isFirstMethod 
                    ? t('invoice.paymentMethods.alternative') 
                    : t('invoice.paymentMethods.online')}
              </p>
              <button className="btn-primary min-w-[200px] justify-center">
                <span>
                  {showMethodInButton ? `${t('invoice.paymentMethods.payNow')} - Mollie` : t('invoice.paymentMethods.payNow')}
                </span>
              </button>
            </div>
          );
        case 'stripe':
          if (!currentInvoice.paymentMethods?.stripe) return null;
          return (
            <div key="stripe" className="invoice-payment-details text-gray-600">
              <p className="text-sm mb-3">
                {hasOnlyOnlinePayments && isFirstMethod
                  ? t('invoice.paymentMethods.onlineOnly', { days: paymentTermsDays })
                  : hasMultipleMethods && !isFirstMethod 
                    ? t('invoice.paymentMethods.alternative') 
                    : t('invoice.paymentMethods.online')}
              </p>
              <button className="btn-primary min-w-[200px] justify-center">
                <span>
                  {showMethodInButton ? `${t('invoice.paymentMethods.payNow')} - Stripe` : t('invoice.paymentMethods.payNow')}
                </span>
              </button>
            </div>
          );
        default:
          return null;
      }
    });
  };

  const renderMethodToggle = (methodType: PaymentMethodType) => {
    const isEnabled = paymentMethods[methodType as keyof PaymentMethods]?.enabled || false;
    const isDefault = defaultMethods.includes(methodType);
    
    return (
      <div 
        key={methodType} 
        className="py-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-gray-900">
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {t(`invoice.paymentMethods.${methodType}.title` as any)}
          </h3>
          
          {/* Apple-style toggle */}
          <button
            type="button"
            onClick={() => handleFieldChange(methodType, 'enabled', !isEnabled)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
              isEnabled ? 'bg-primary-600' : 'bg-gray-200'
            }`}
            aria-pressed={isEnabled}
            aria-labelledby={`${methodType}-label`}
          >
            <span className="sr-only">{t('invoice.paymentMethods.enabled')}</span>
            <span
              className={`pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                isEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            >
              <span
                className={`absolute inset-0 flex h-full w-full items-center justify-center transition-opacity ${
                  isEnabled ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in'
                }`}
              />
              <span
                className={`absolute inset-0 flex h-full w-full items-center justify-center transition-opacity ${
                  isEnabled ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out'
                }`}
              >
                <Check className="h-3 w-3 text-primary-600" />
              </span>
            </span>
          </button>
        </div>
        
        {/* Form fields */}
        <AnimatePresence>
          {isEnabled && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-4"
            >
              {methodType === 'bank' && (
                <div className="space-y-4">
                  <FloatingLabelInput
                    name="iban"
                    type="text"
                    value={paymentMethods.bank?.iban || ''}
                    onChange={(e) => handleFieldChange(methodType, 'iban', e.target.value)}
                    label={t('invoice.paymentMethods.bank.iban')}
                    className="bg-gray-50 border-0 rounded-lg"
                  />
                  <FloatingLabelInput
                    name="bic"
                    type="text"
                    value={paymentMethods.bank?.bic || ''}
                    onChange={(e) => handleFieldChange(methodType, 'bic', e.target.value)}
                    label={t('invoice.paymentMethods.bank.bic')}
                    className="bg-gray-50 border-0 rounded-lg"
                  />
                  <FloatingLabelInput
                    name="accountName"
                    type="text"
                    value={paymentMethods.bank?.accountName || ''}
                    onChange={(e) => handleFieldChange(methodType, 'accountName', e.target.value)}
                    label={t('invoice.paymentMethods.bank.accountName')}
                    className="bg-gray-50 border-0 rounded-lg"
                  />
                </div>
              )}
              
              {methodType === 'paypal' && (
                <div className="space-y-4">
                  <FloatingLabelInput
                    name="email"
                    type="email"
                    value={paymentMethods.paypal?.email || ''}
                    onChange={(e) => handleFieldChange(methodType, 'email', e.target.value)}
                    label={t('invoice.paymentMethods.paypal.email')}
                    className="bg-gray-50 border-0 rounded-lg"
                  />
                </div>
              )}
              
              {methodType === 'mollie' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    {/* Check for isConnected in the actual settings instead of local paymentMethods */}
                    {settings?.paymentMethods?.mollie?.isConnected ? (
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
        
                            <div className="pl-2">
                              <span className="text-sm font-medium text-gray-900">{t('invoice.paymentMethods.mollie.connected')}</span>
                              {/* Use settings.paymentMethods for the businessName */}
                              {settings?.paymentMethods?.mollie && 'businessName' in settings.paymentMethods.mollie && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {/* eslint-disable-next-line */}
                                  {(settings.paymentMethods.mollie as any).businessName}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleMollieConnect()}
                            className="text-sm text-primary-600 hover:text-primary-800 flex items-center gap-1 transition-colors"
                          >
                            {t('invoice.paymentMethods.mollie.reconnect')}
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        </div>
                        <div className="text-xs text-gray-600 bg-white rounded-md p-2 border border-gray-100">
                          <p className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse-subtle"></span>
                            {t('invoice.paymentMethods.mollie.readyToReceive')}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <p className="text-sm text-gray-600 flex-1">
                          {t('invoice.paymentMethods.mollie.explain')}
                        </p>
                        <button
                          type="button"
                          onClick={() => handleMollieConnect()}
                          disabled={isMollieConnecting}
                          className="btn-primary text-sm whitespace-nowrap flex items-center px-4 gap-2"
                        >
                          {isMollieConnecting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              {t('invoice.paymentMethods.mollie.connecting')}
                            </>
                          ) : (
                            <>
                              <ExternalLink className="w-4 h-4" />
                              {t('invoice.paymentMethods.mollie.connect')}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {methodType === 'stripe' && (
                <div className="space-y-4">
                  <FloatingLabelInput
                    name="accountId"
                    type="text"
                    value={paymentMethods.stripe?.accountId || ''}
                    onChange={(e) => handleFieldChange(methodType, 'accountId', e.target.value)}
                    label={t('invoice.paymentMethods.stripe.accountId')}
                    className="bg-gray-50 border-0 rounded-lg"
                  />
                  <FloatingLabelInput
                    name="description"
                    type="text"
                    value={paymentMethods.stripe?.description || ''}
                    onChange={(e) => handleFieldChange(methodType, 'description', e.target.value)}
                    label={t('invoice.paymentMethods.stripe.description')}
                    className="bg-gray-50 border-0 rounded-lg"
                  />
                </div>
              )}
              
              {/* Add "Add as default for future invoices" checkbox */}
              <div className="mt-4 flex items-center">
                <input
                  id={`default-${methodType}`}
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => toggleDefaultMethod(methodType, e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor={`default-${methodType}`} className="ml-2 block text-sm text-gray-600">
                  {t('invoice.paymentMethods.setAsDefault')}
                </label>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const renderEditContent = () => (
    <div className="space-y-2 max-w-2xl mx-auto bg-white rounded-xl">
      <div className="divide-y divide-gray-100">
        {PAYMENT_METHOD_TYPES.filter(methodType => !TEMP_HIDE_PAYMENTS.includes(methodType)).map((methodType) => {
          return renderMethodToggle(methodType);
        })}
      </div>
    </div>
  );

  // If there's no payment method yet, just show the "Add Payment Method" link
  if (!hasPaymentMethods && !isEditing) {
    return (
      <div className="flex p-4 justify-end">
        <button 
          onClick={onEditClick}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
          data-pdf-exclude="true"
        >
          {t('invoice.paymentMethods.add')}
        </button>
      </div>
    );
  }

  // If there's a payment method or we're editing, use InlineEdit
  return (
    <div className="relative py-2.5">
    <InlineEdit
      isEditing={isEditing}
      onEditClick={onEditClick}
      onSave={handleSave}
      displayContent={renderPaymentMethodDisplay()}
      editContent={renderEditContent()}
    />
    
    <LoginRequiredDialog
      isOpen={showMollieLoginDialog}
      onClose={() => setShowMollieLoginDialog(false)}
      feature="mollie"
    />
    </div>
  );
};

export default PaymentMethodEdit; 